import { cva } from "class-variance-authority";

/**
 * Registration flow layout — responsive shell matching login/download-app patterns.
 * Figma: ACP — Registration Redesign 2026 — Handoff (node 4001:59883, email mobile).
 */
export const registerFlowRootStyles = cva([
  "w-full",
  "flex",
  "flex-col",
  "items-center",
  "px-4",
  "pt-8",
  "md:px-8",
  "lg:px-0",
]);

/** Figma 4001-59918: 32px stepper-to-header gap on mobile; 64px from md+ */
export const registerStepperWrapperStyles = cva([
  "w-full",
  "md:max-w-[396px]",
  "lg:max-w-[476px]",
  "mx-auto",
  "mb-8",
  "md:mb-16",
]);

export const registerStepCardStyles = cva([
  "w-full",
  "p-0",
  "mb-4",
  "md:rounded-2xl",
  "md:bg-white",
  "md:shadow-[0_1px_3px_0_rgba(0,0,0,0.10)]",
  "md:p-8",
  "md:max-w-[396px]",
  "lg:max-w-[476px]",
  "md:mx-auto",
]);

export const registerStepFormStyles = cva([
  "flex",
  "flex-col",
  "gap-4",
  "md:gap-6",
  "w-full",
]);

export const registerSecurityBadgeStyles = cva(["mt-6"]);

export const registerStepHeaderContainerStyles = cva([
  "w-full",
  "md:max-w-[396px]",
  "lg:max-w-[476px]",
  "mx-auto",
  "mb-8",
  "flex",
  "flex-col",
  "gap-4",
]);

export const registerStepHeaderTitleStyles = cva([
  "m-0",
  "text-left",
  "font-medium",
  "text-[#1B1C1D]",
  "font-[Poppins,sans-serif]",
  "text-[24px]",
  "leading-[32px]",
  "tracking-normal",
  "md:text-[32px]",
  "md:leading-[40px]",
]);

export const registerStepHeaderSubtitleStyles = cva([
  "m-0",
  "text-left",
  "text-[#44474C]",
  "font-[Poppins,sans-serif]",
  "text-sm",
  "leading-5",
  "font-normal",
  "tracking-[0.25px]",
  "md:text-base",
  "md:leading-6",
  "md:tracking-[0.5px]",
]);

export const registerOtpHeaderStyles = cva([
  "w-full",
  "md:max-w-[396px]",
  "lg:max-w-[476px]",
  "mx-auto",
  "mb-6",
  "text-left",
  "font-[Poppins,sans-serif]",
  "text-[#1B1C1D]",
  "text-base",
  "leading-6",
  "font-medium",
  "tracking-[0.5px]",
  "md:text-[22px]",
  "md:leading-7",
  "md:tracking-normal",
]);

/** OTP body inside register card — shell provides horizontal padding */
export const registerOtpBodyStyles = cva(["register-otp", "w-full"]);

/** Override global mfa-code-content-wrapper mobile bottom margin */
export const registerOtpContentWrapperStyles = cva(["!mb-0", "md:!mb-6"]);

/** OTP stack — left-aligned on mobile; centered from md+ */
export const registerOtpStackStyles = cva([
  "max-md:w-full",
  "!mx-0",
  "!items-start",
  "md:!mx-auto",
  "md:!items-center",
]);

/** OTP digit row — MFACodeContainerV2 mobile only: full width + distributed cells */
export const registerOtpInputMobileStyles = cva([
  "!w-full",
  "!max-w-full",
  "!justify-between",
  "!gap-2",
]);

/** OTP digit row — md+ uses MFACodeInput defaults with centered fixed-width row */
export const registerOtpInputStyles = cva([
  "md:!justify-center",
  "md:!w-fit",
  "md:!gap-3",
  "mb-4",
  "md:!mb-0",
]);

/** OTP digit cells — MFACodeContainerV2 mobile only: flex to fit narrow viewports */
export const registerOtpDigitMobileStyles = cva([
  "!w-auto",
  "min-w-0",
  "flex-1",
  "basis-0",
  "max-w-12",
]);

export const registerErrorBannerStyles = cva([
  "w-full",
  "mb-4",
  "md:max-w-[396px]",
  "lg:max-w-[476px]",
  "mx-auto",
]);

export const REGISTER_STEPPER_CIRCLE_SIZE = { mobile: 40, desktop: 56 } as const;
export const REGISTER_STEPPER_GLYPH_SIZE = { mobile: 18, desktop: 32 } as const;
export const REGISTER_STEPPER_LABEL_OFFSET = 8;
export const REGISTER_STEPPER_LABEL_LINE_HEIGHT = { mobile: 20, desktop: 24 } as const;

/** Stepper glyph icons — Figma mobile: 18×18; md+: 32×32 */
export const registerStepperGlyphStyles = cva([
  "shrink-0",
  "aspect-square",
  "size-[18px]",
  "md:size-8",
]);

/** Stepper step circles — mobile: 40px; md+: 56px */
export const registerStepperCircleStyles = cva([
  "rounded-full",
  "flex",
  "items-center",
  "justify-center",
  "shrink-0",
  "aspect-square",
  "size-10",
  "md:size-14",
  "transition-all",
  "duration-300",
]);

/** Back control — mobile: 18px; md+: 40px */
export const registerStepperBackButtonStyles = cva([
  "shrink-0",
  "aspect-square",
  "p-0",
  "size-[18px]",
  "md:size-10",
]);

/** Connector line between step circles */
export const registerStepperConnectorStyles = cva(["h-1", "bg-[#E5E7EB]"]);

/** Step label — body/medium on mobile; body/large on md+ */
export const registerStepperLabelStyles = cva([
  "m-0",
  "whitespace-nowrap",
  "text-center",
  "transition-colors",
  "duration-300",
  "font-[Poppins,sans-serif]",
  "text-[#44474C]",
  "text-sm",
  "leading-5",
  "font-normal",
  "tracking-[0.25px]",
  "md:text-base",
  "md:leading-6",
  "md:tracking-[0.5px]",
]);

/** Circle state colors — Figma 4058-13583; inactive icon fill #1B1C1D, gray border unchanged */
export const registerStepperCircleStateStyles = cva([], {
  variants: {
    state: {
      completed: "border-2 border-[#35CCBA] bg-[#35CCBA] text-[#1B1C1D]",
      current: "bg-[#35CCBA] text-[#1B1C1D] shadow-[0_0_0_4px_rgba(53,204,186,0.10)]",
      inactive:
        "border-2 border-[#C4C7C5] bg-white text-[#1B1C1D] md:border-gray-300",
    },
  },
  defaultVariants: {
    state: "inactive",
  },
});
